import React, { useState, useEffect, useCallback } from 'react';
import { TrendingUp, Download, Loader2, ChevronDown, ChevronRight, FileText } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import api from '../lib/api';

const eur = (cents) => (cents / 100).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });

const RevenueReport = () => {
  const [months, setMonths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [expandedMonths, setExpandedMonths] = useState([]);

  const toggleMonth = (month) => {
    setExpandedMonths((prev) =>
      prev.includes(month) ? prev.filter((m) => m !== month) : [...prev, month]
    );
  };

  const loadReport = useCallback(async () => {
    try {
      const response = await api.get('/invoices/revenue-report');
      setMonths(response.data.months || []);
    } catch (err) {
      toast.error('Error al cargar la relación de ingresos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const downloadCSV = async (month) => {
    setDownloading(month || 'all');
    try {
      const response = await api.get('/invoices/revenue-report/csv', {
        params: month ? { month } : {},
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = month ? `relacion_ingresos_${month}.csv` : 'relacion_ingresos_completa.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Descarga iniciada');
    } catch (err) {
      toast.error('Error al descargar el CSV');
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="card-dark p-6" data-testid="revenue-report-section">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-[#FFD700]" />
          <h3 className="text-lg font-semibold text-white">Relación de Ingresos (facturas emitidas)</h3>
        </div>
        {months.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => downloadCSV(null)}
            disabled={downloading === 'all'}
            data-testid="download-all-revenue-btn"
          >
            {downloading === 'all' ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Descargar todo (CSV)
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : months.length === 0 ? (
        <div className="text-center py-8 text-zinc-500">No hay facturas emitidas todavía</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="revenue-report-table">
            <thead>
              <tr className="text-zinc-400 border-b border-white/10">
                <th className="text-left py-2 pr-4 font-medium">Mes</th>
                <th className="text-right py-2 px-4 font-medium">Nº Facturas</th>
                <th className="text-right py-2 px-4 font-medium hidden sm:table-cell">Base Imponible</th>
                <th className="text-right py-2 px-4 font-medium">IVA</th>
                <th className="text-right py-2 px-4 font-medium">Total Facturado</th>
                <th className="text-right py-2 pl-4 font-medium">CSV</th>
              </tr>
            </thead>
            <tbody>
              {months.map((m) => {
                const isExpanded = expandedMonths.includes(m.month);
                return (
                  <React.Fragment key={m.month}>
                    <tr
                      className="border-b border-white/5 hover:bg-white/5 transition-colors cursor-pointer"
                      onClick={() => toggleMonth(m.month)}
                      data-testid={`revenue-month-row-${m.month}`}
                    >
                      <td className="py-3 pr-4 text-white capitalize">
                        <span className="flex items-center gap-1.5">
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4 text-[#FFD700]" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-zinc-500" />
                          )}
                          {m.month_label}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right text-zinc-300">{m.invoice_count}</td>
                      <td className="py-3 px-4 text-right text-zinc-300 hidden sm:table-cell">{eur(m.subtotal_cents)}</td>
                      <td className="py-3 px-4 text-right text-zinc-300">{eur(m.tax_cents)}</td>
                      <td className="py-3 px-4 text-right text-[#FFD700] font-semibold">{eur(m.total_cents)}</td>
                      <td className="py-3 pl-4 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadCSV(m.month);
                          }}
                          disabled={downloading === m.month}
                          data-testid={`download-revenue-${m.month}`}
                        >
                          {downloading === m.month ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Download className="w-4 h-4" />
                          )}
                        </Button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="border-b border-white/5 bg-black/30">
                        <td colSpan={6} className="py-2 px-4">
                          <div className="pl-6 py-1 space-y-1" data-testid={`revenue-month-detail-${m.month}`}>
                            {(m.invoices || []).map((inv) => (
                              <div
                                key={inv.invoice_number}
                                className="flex items-center justify-between text-xs text-zinc-400 py-1 border-b border-white/5 last:border-0"
                              >
                                <span className="flex items-center gap-2">
                                  <FileText className="w-3 h-3 text-zinc-600" />
                                  {inv.invoice_number}
                                </span>
                                <span className="text-zinc-300">{eur(inv.total_cents)}</span>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default RevenueReport;
